import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AdminGatewayPayments() {
  const [payments, setPayments] = useState([]);
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [message, setMessage] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState('');
  const [comment, setComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadPayments();
    loadStats();
  }, [filter]);

  async function loadPayments() {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const { data } = await api.get(`/gateway-payments/admin/list${params}`);
      setPayments(data.payments || []);
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const { data } = await api.get('/gateway-payments/admin/stats');
      setStats(data);
    } catch (e) {}
  }

  function openAction(payment, act) {
    setSelectedPayment(payment);
    setAction(act);
    setComment('');
    setRejectionReason('');
    setShowModal(true);
  }

  async function handleAction() {
    setActionLoading(true);
    try {
      await api.post('/gateway-payments/admin/verify', {
        payment_id: selectedPayment.id,
        action,
        comment: comment || null,
        rejection_reason: rejectionReason || null,
      });
      setMessage({ type: 'success', text: `Платёж ${action === 'confirm' ? 'подтверждён' : action === 'reject' ? 'отклонён' : 'сброшен'}` });
      setShowModal(false);
      loadPayments();
      loadStats();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setActionLoading(false);
    }
  }

  const statusColors = {
    unpaid: '#6b7280',
    pending_verification: '#f59e0b',
    paid_confirmed: '#10b981',
    rejected: '#ef4444',
  };

  const statusLabels = {
    unpaid: 'Не оплачен',
    pending_verification: 'Ожидает проверки',
    paid_confirmed: 'Подтверждён',
    rejected: t('withdrawals.status_rejected'),
  };

  return (
    <div className="page"><BackButton />
      <h1>💳 Платежи Gateway</h1>

      {message && (
        <div className={`banner ${message.type === 'error' ? 'banner-warning' : 'banner-success'}`} 
             style={{ marginBottom: 16 }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div className="detail-card" style={{ flex: 1, minWidth: 120, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 24, color: '#f59e0b' }}>{stats.pending_verification || 0}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Ожидает</div>
          </div>
          <div className="detail-card" style={{ flex: 1, minWidth: 120, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 24, color: '#10b981' }}>{stats.by_status?.paid_confirmed?.count || 0}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Подтверждено</div>
          </div>
          <div className="detail-card" style={{ flex: 1, minWidth: 120, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 24 }}>{stats.by_status?.paid_confirmed?.total || 0} PLN</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Всего получено</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {['all', 'pending_verification', 'paid_confirmed', 'unpaid', 'rejected'].map(f => (
          <button
            key={f}
            className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '6px 12px', fontSize: 13 }}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'Все' : statusLabels[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : payments.length === 0 ? (
        <p style={{ opacity: 0.5 }}>Нет платежей</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {payments.map(p => (
            <div key={p.id} className="detail-card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <b>{p.user_name || 'Unknown'}</b>
                  <span style={{ opacity: 0.5, marginLeft: 8, fontSize: 12 }}>TG: {p.telegram_id}</span>
                </div>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: 12,
                  background: statusColors[p.status],
                }}>
                  {statusLabels[p.status]}
                </span>
              </div>
              
              <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>
                <div><b>Тип:</b> {p.payment_type === 'activation' ? 'Активация' : 'Подписка'}</div>
                <div><b>Сумма:</b> {p.amount} {p.currency}</div>
                <div><b>Назначение:</b> {p.transfer_title}</div>
                {p.confirmed_at && <div><b>Подтверждено клиентом:</b> {new Date(p.confirmed_at).toLocaleString()}</div>}
                {p.confirmation_file && (
                  <div>
                    <b>Файл:</b>{' '}
                    <a href={`https://api.workifypl.com${p.confirmation_file}`} target="_blank" rel="noreferrer" style={{ color: '#7c3aed' }}>
                      Скачать
                    </a>
                  </div>
                )}
                {p.admin_comment && <div><b>Комментарий:</b> {p.admin_comment}</div>}
                {p.rejection_reason && <div style={{ color: '#ef4444' }}><b>Причина отказа:</b> {p.rejection_reason}</div>}
              </div>

              {/* Actions */}
              {p.status === 'pending_verification' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn"
                    style={{ padding: '6px 12px', fontSize: 12, background: '#10b981' }}
                    onClick={() => openAction(p, 'confirm')}
                  >
                    ✅ Подтвердить
                  </button>
                  <button
                    className="btn"
                    style={{ padding: '6px 12px', fontSize: 12, background: '#ef4444' }}
                    onClick={() => openAction(p, 'reject')}
                  >
                    ❌ Отклонить
                  </button>
                </div>
              )}
              {(p.status === 'rejected' || p.status === 'paid_confirmed') && (
                <button
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={() => openAction(p, 'reset')}
                >
                  ↻ Сбросить
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      {showModal && selectedPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 16,
        }}>
          <div style={{
            background: '#1a1a2e',
            borderRadius: 12,
            padding: 24,
            maxWidth: 400,
            width: '100%',
          }}>
            <h2 style={{ marginTop: 0 }}>
              {action === 'confirm' && '✅ Подтвердить платёж'}
              {action === 'reject' && '❌ Отклонить платёж'}
              {action === 'reset' && '↻ Сбросить платёж'}
            </h2>
            
            <p>
              Пользователь: <b>{selectedPayment.user_name}</b><br />
              Сумма: <b>{selectedPayment.amount} PLN</b>
            </p>

            {action === 'reject' && (
              <label style={{ display: 'block', marginBottom: 12 }}>
                <span style={{ display: 'block', marginBottom: 4 }}>Причина отказа:</span>
                <input
                  type="text"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Не найден перевод..."
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: '#fff',
                  }}
                />
              </label>
            )}

            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ display: 'block', marginBottom: 4 }}>Комментарий (опционально):</span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                }}
              />
            </label>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={actionLoading}
              >
                Отмена
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAction}
                disabled={actionLoading}
              >
                {actionLoading ? '⏳...' : 'Применить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
